import React, { useState, useEffect, useCallback } from 'react'
import { runCustomReport, exportCustomReport } from '../../api/customReportsApi'
import ReportChartRenderer from './ReportChartRenderer'
import DrillDownCell from './DrillDownCell'
import AppPagination from '../../../../components/AppPagination'
import AppActionMenu from '../../../../components/AppActionMenu'
import { appToast } from '../../../../components/AppToast'
import { normalizeReportError } from '../../utils/reportErrorUtils'

export default function CustomReportViewModal({
  reportId,
  open,
  onClose,
  onEdit,
  onPrint,
}) {
  const [reportResult, setReportResult] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(50)

  const loadData = useCallback(async () => {
    if (!reportId) return
    setLoading(true)
    setError(null)
    try {
      const data = await runCustomReport(reportId, { page, pageSize })
      setReportResult(data)
    } catch (err) {
      setError(normalizeReportError(err, 'Failed to run custom report.'))
    } finally {
      setLoading(false)
    }
  }, [reportId, page, pageSize])

  useEffect(() => {
    if (open && reportId) {
      loadData()
    }
  }, [open, reportId, loadData])

  async function handleExport(format) {
    if (!reportId) return
    try {
      const { blob, filename } = await exportCustomReport(reportId, format)
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
    } catch (err) {
      appToast.error(normalizeReportError(err, 'Export failed'))
    }
  }

  if (!open) return null

  const columns = reportResult?.columns || []
  const rows = reportResult?.rows || []
  const cards = reportResult?.cards || []
  const chartData = reportResult?.chartData || []
  const visualization = reportResult?.visualization || {}
  const totalRows = reportResult?.summary?.totalRows || 0

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60 p-3 backdrop-blur-sm animate-fade-in">
      <div className="flex flex-col w-full max-w-5xl h-[88vh] max-h-[840px] bg-surface rounded-[12px] shadow-2xl overflow-hidden border border-surface-container">
        {/* Header */}
        <div className="shrink-0 bg-surface-container-low border-b border-surface-container px-4 py-2.5 flex items-center justify-between">
          <div className="min-w-0 pr-2">
            <div className="flex items-center gap-1.5">
              <span className="text-[9px] font-extrabold uppercase tracking-wider bg-primary/10 text-primary px-1.5 py-0.2 rounded-[4px]">
                Custom Report
              </span>
              <h2 className="text-base font-headline font-extrabold text-on-surface truncate">
                {reportResult?.reportName || 'Loading Report...'}
              </h2>
            </div>
            {reportResult?.description && (
              <p className="text-[11px] text-on-surface-variant truncate mt-0.5">{reportResult.description}</p>
            )}
          </div>

          <div className="flex items-center gap-1.5 shrink-0">
            <button
              type="button"
              onClick={loadData}
              className="p-1 text-slate-500 hover:text-on-surface hover:bg-surface-container rounded-[8px]"
              title="Refresh"
            >
              <span className="material-symbols-outlined text-sm">refresh</span>
            </button>
            <button
              type="button"
              onClick={() => onPrint?.(reportResult)}
              className="flex items-center gap-1 px-2.5 py-1 rounded-[8px] border border-surface-container-high text-xs font-semibold hover:bg-surface-container text-on-surface"
              title="Print"
            >
              <span className="material-symbols-outlined text-xs">print</span>
              Print
            </button>
            <AppActionMenu
              triggerLabel="Export"
              triggerClassName="flex items-center gap-1 px-2.5 py-1 rounded-[8px] border border-surface-container-high text-xs font-semibold hover:bg-surface-container text-on-surface"
              items={[
                { key: 'xlsx', label: 'Excel (.xlsx)', icon: 'table_view' },
                { key: 'xls', label: 'Legacy Excel (.xls)', icon: 'table_view' },
                { key: 'csv', label: 'CSV (.csv)', icon: 'description' },
                { key: 'pdf', label: 'PDF (.pdf)', icon: 'picture_as_pdf' },
              ]}
              onSelect={(key) => handleExport(key)}
            />
            {onEdit && (
              <button
                type="button"
                onClick={() => {
                  onClose()
                  onEdit(reportId)
                }}
                className="flex items-center gap-1 px-2.5 py-1 rounded-[8px] bg-surface-container-high text-xs font-semibold text-on-surface hover:bg-surface-container-highest"
              >
                <span className="material-symbols-outlined text-xs">edit</span>
                Edit Builder
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="p-1 text-on-surface-variant hover:text-on-surface hover:bg-surface-container rounded-full"
            >
              <span className="material-symbols-outlined text-base">close</span>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-3.5 space-y-3">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-64 space-y-3">
              <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin"></div>
              <p className="text-xs font-semibold text-on-surface-variant">Executing report query across datasets...</p>
            </div>
          ) : error ? (
            <div className="p-6 bg-red-50/60 border border-red-200/80 rounded-[12px] text-sm text-center space-y-2 max-w-md mx-auto my-12">
              <h4 className="font-bold text-base text-on-surface">Report Execution Error</h4>
              <p className="text-xs text-on-surface-variant">{error}</p>
            </div>
          ) : (
            <>
              {/* Metric Cards */}
              {cards.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {cards.map((c) => (
                    <div key={c.key} className="p-3.5 bg-surface-container-low rounded-[10px] border border-surface-container">
                      <span className="text-[10px] uppercase font-bold text-on-surface-variant tracking-wider block truncate">
                        {c.label}
                      </span>
                      <span className="text-lg font-headline font-extrabold text-primary mt-0.5 block">
                        {c.format === 'currency' ? `₹${Number(c.value).toLocaleString()}` : c.value}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Chart Renderer */}
              <ReportChartRenderer
                visualization={visualization}
                chartData={chartData}
                summaryCards={cards}
              />

              {/* Table */}
              <div className="bg-surface rounded-[10px] border border-surface-container overflow-hidden shadow-sm">
                <div className="overflow-x-auto max-h-[500px]">
                  <table className="w-full text-left text-xs border-collapse">
                    <thead className="bg-surface-container-low sticky top-0 z-10 border-b border-surface-container">
                      <tr>
                        <th className="py-2.5 px-3 font-extrabold text-[10px] uppercase text-on-surface-variant tracking-wider w-10">
                          #
                        </th>
                        {columns.map((col) => (
                          <th
                            key={col.key}
                            className="py-2.5 px-3 font-extrabold text-[10px] uppercase text-on-surface-variant tracking-wider"
                          >
                            {col.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-surface-container-low">
                      {rows.length === 0 ? (
                        <tr>
                          <td colSpan={columns.length + 1} className="py-12 text-center text-on-surface-variant text-xs">
                            No records match the selected criteria.
                          </td>
                        </tr>
                      ) : (
                        rows.map((row, rIdx) => (
                          <tr key={rIdx} className="hover:bg-surface-container-lowest transition-colors">
                            <td className="py-2 px-3 text-slate-400 text-[11px] font-mono">
                              {(page - 1) * pageSize + rIdx + 1}
                            </td>
                            {columns.map((col) => {
                              const val = row[col.key]
                              return (
                                <td key={col.key} className="py-2 px-3 text-on-surface">
                                  {col.drillDownType ? (
                                    <DrillDownCell value={val} drillDownType={col.drillDownType} row={row} />
                                  ) : col.dataType === 'currency' ? (
                                    `₹${Number(val || 0).toLocaleString()}`
                                  ) : col.dataType === 'percentage' ? (
                                    `${val}%`
                                  ) : (
                                    String(val ?? '—')
                                  )}
                                </td>
                              )
                            })}
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pagination info */}
              <div className="flex items-center justify-between text-xs text-on-surface-variant pt-2 border-t border-surface-container">
                <span>
                  Showing {rows.length} of {totalRows.toLocaleString()} total records
                </span>
                <AppPagination
                  page={page}
                  totalPages={Math.max(1, Math.ceil(totalRows / pageSize))}
                  onPageChange={(p) => setPage(p)}
                  onPrevious={() => setPage((prev) => Math.max(1, prev - 1))}
                  onNext={() => setPage((prev) => Math.min(Math.max(1, Math.ceil(totalRows / pageSize)), prev + 1))}
                  canPrevious={page > 1}
                  canNext={page < Math.ceil(totalRows / pageSize)}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
